import React from 'react';

import {RegionalReportResponse} from 'src/lib/api/regionalReport';

import {percentColor} from './colors';

const EXCEEDANCE_COLUMNS = [
  {quantile: 'discharge_q0.1', label: '90%'},
  {quantile: 'discharge_q0.25', label: '75%'},
  {quantile: 'discharge_q0.5', label: '50%'},
  {quantile: 'discharge_q0.75', label: '25%'},
  {quantile: 'discharge_q0.9', label: '10%'},
];

type Props = {
  report: RegionalReportResponse;
};

export default function SynopsisTable({report}: Props) {
  const {sites, targetPeriods, synopsis, aggregated} = report;

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b-2 border-gray-300">
            <th className="text-left py-2 px-3 font-semibold">Forecast Location</th>
            <th className="text-left py-2 px-3 font-semibold">Target Period</th>
            <th colSpan={5} className="text-center py-2 px-3 font-semibold">
              Exceedance Probability
            </th>
          </tr>
          <tr className="border-b border-gray-200">
            <th />
            <th />
            {EXCEEDANCE_COLUMNS.map((col) => (
              <th key={col.quantile} className="text-right py-1 px-3 font-medium text-gray-600">
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sites.map((site) => {
            const siteSynopsis = synopsis[site.siteId];
            return targetPeriods.map((tp, tpIdx) => {
              const tpData = siteSynopsis?.[tp.label];
              return (
                <tr
                  key={`${site.siteId}-${tp.label}`}
                  className={`${tpIdx === targetPeriods.length - 1 ? 'border-b border-gray-200' : ''}`}
                >
                  <td className="py-1 px-3 font-medium">
                    {tpIdx === 0 ? (
                      <a className="text-blue-700 hover:underline" href={`#site-${site.siteId}`}>
                        {site.name}
                      </a>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="py-1 px-3 text-gray-700">
                    {tp.label}
                    {tp.isPartial && (
                      <span className="ml-1 text-[10px] italic text-gray-400">(to date)</span>
                    )}
                  </td>
                  {EXCEEDANCE_COLUMNS.map((col) => {
                    const val = tpData?.percentOfNormal?.[col.quantile] ?? null;
                    return (
                      <td
                        key={col.quantile}
                        className={`py-1 px-3 text-right tabular-nums ${percentColor(val)}`}
                      >
                        {val !== null ? Math.round(val) : '—'}
                      </td>
                    );
                  })}
                </tr>
              );
            });
          })}
          {/* Aggregated row */}
          {targetPeriods.map((tp, tpIdx) => {
            const aggData = aggregated[tp.label];
            return (
              <tr
                key={`agg-${tp.label}`}
                className={`${tpIdx === 0 ? 'border-t-2 border-gray-400' : ''} bg-gray-50 font-semibold`}
              >
                <td className="py-1 px-3">{tpIdx === 0 ? 'All Sites' : ''}</td>
                <td className="py-1 px-3 text-gray-700">
                  {tp.label}
                  {aggData?.siteCount != null && aggData.siteCount < report.sitesIncluded && (
                    <span className="ml-1 text-[10px] font-normal text-gray-500">
                      ({aggData.siteCount} of {report.sitesIncluded} sites)
                    </span>
                  )}
                  {tp.isPartial && (
                    <span className="ml-1 text-[10px] italic text-gray-400">(to date)</span>
                  )}
                </td>
                {EXCEEDANCE_COLUMNS.map((col) => {
                  const val = aggData?.percentOfNormal?.[col.quantile] ?? null;
                  return (
                    <td
                      key={col.quantile}
                      className={`py-1 px-3 text-right tabular-nums ${percentColor(val)}`}
                    >
                      {val !== null ? Math.round(val) : '—'}
                    </td>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
